import { dictionarySection } from "@/content/i18n/dictionaries";
import type { Locale } from "@/lib/i18n/config";

export type NavLink = {
  label: string;
  href: string;
};

export type FooterLink = {
  label: string;
  href: string;
  consentPreferences?: boolean;
};

type SiteContent = {
  skipToContentLabel: string;
  meta: {
    title: string;
    description: string;
    keywords: string[];
    openGraphTitle: string;
    openGraphDescription: string;
    twitterTitle: string;
    twitterDescription: string;
  };
  common: {
    languageLabel: string;
    selectLanguageGroupLabel: string;
    backToTopLabel: string;
    themeLightLabel: string;
    themeDarkLabel: string;
  };
  cookieConsent: {
    ariaLabel: string;
    body: string;
    policyLinkLabel: string;
    rejectAll: string;
    preferences: string;
    acceptAll: string;
    necessary: string;
    analytics: string;
    marketing: string;
    save: string;
    gpcNotice: string;
    gpcNoticeLinkLabel: string;
  };
  faq: {
    eyebrow: string;
    sectionsNavAriaLabel: string;
    questionsAriaSuffix: string;
  };
  seo: {
    softwareApplication: {
      description: string;
      offersDescription: string;
    };
  };
  nav: {
    brandLabel: string;
    ariaLabel: string;
    changeLanguageLabel: string;
    links: NavLink[];
    ctaPrimary: { label: string; href: string };
    ctaWaitlist: { label: string; href: string };
    locales: readonly { code: string; label: string; value: Locale }[];
    mobileOpenLabel: string;
    mobileCloseLabel: string;
    mobileMenuLabel: string;
  };
  hero: {
    slides: {
      eyebrow?: string;
      titleLines: string[];
      subtitle: string;
      image: string;
      alt: string;
    }[];
    tabsLabel: string;
    slideLabel: string;
    downloadHint: string;
    founderStrip: {
      eyebrow: string;
      places: string;
      placesPending: string;
      line: string;
      ends: string;
      terms: string;
      meterLabel: string;
      cap: string;
      deadline: string;
    };
    promotion: {
      title: string;
      headline: string;
      detailsCue: string;
      subline: string;
      places: string;
      seatsLeft: string;
      summary: string;
      details: string;
      ends: string;
      terms: string;
      meterLabel: string;
      download: string;
      close: string;
    };
    ctaIos: { label: string; prefix: string; storeName: string };
    ctaAndroid: { label: string; prefix: string; storeName: string };
  };
  collaboration: {
    sectionLabel: string;
    heading: string;
    primaryPartner: {
      label: string;
      href: string;
      imageAlt: string;
    };
    secondaryPartners: {
      label: string;
      href: string;
      imageAlt: string;
    }[];
  };
  closedLaunchLine: {
    ariaLabel: string;
    body: string;
  };
  footer: {
    positioning: string;
    headquarters: {
      title: string;
      name: string;
      lines: string[];
      phoneLabel: string;
      phoneHref: string;
      mapLabel: string;
      mapHref: string;
    };
    newsletter: {
      title: string;
      body: string;
    };
    social: {
      title: string;
      links: FooterLink[];
    };
    columns: {
      title: string;
      links: FooterLink[];
    }[];
    contact: {
      title: string;
      triggerLabel: string;
      emailLabel: string;
      emailHref: string;
    };
    copyright: {
      brandName: string;
      rightsReserved: string;
    };
    originLabel: string;
    companyIdentity: {
      cifLabel: string;
      vatLabel: string;
    };
    legal: FooterLink[];
  };
};

export function getSiteContent(locale: Locale): SiteContent {
  return dictionarySection(locale, "site") as SiteContent;
}

export const nav = getSiteContent("en").nav;
export const hero = getSiteContent("en").hero;
export const footer = getSiteContent("en").footer;
